<template>
  <v-container fluid class="px-6 px-sm-12">
    <PageHeader
      :header-text="'User Admin'"
      :header-icon="'mdi-account-group'"
      :disableSiteChange="true"
    />

    <div v-if="selectedUser && selectedUser.username">
      <UserEditForm v-bind:user="selectedUser"></UserEditForm>
    </div>

    <v-card class="mt-5 mx-auto">
      <v-card-title class="primary--text">
        <v-icon color="primary" left>mdi-account-group</v-icon>
        Users
        <v-spacer></v-spacer>
        <v-text-field
          class="ma-0 mr-4 pa-0 search-field"
          v-model="search"
          label="Search..."
          prepend-inner-icon="mdi-magnify"
          single-line
          hide-details
          clearable
          outlined
          dense
        ></v-text-field>
        <v-dialog v-model="dialog" max-width="850px">
          <template v-slot:activator="{ on }">
            <v-btn color="primary" v-on="on">
              <v-icon left>mdi-plus</v-icon>Add
            </v-btn>
          </template>
          <v-card>
            <v-card-title class="primary--text">
              <v-icon color="primary" left>mdi-account</v-icon>
              Add User
            </v-card-title>
            <v-card-text>
              <UserCreateForm :onCloseClick="close"></UserCreateForm>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-card-title>
      <v-card-text>
        <v-data-table
          dense
          :items="dataForTable"
          :headers="tableHeaders"
          :mobile-breakpoint="dataTableMobileBreakpoint"
          :items-per-page="-1"
          :loading="usersLoading"
          sort-by="roles"
          :sort-desc="true"
          :search="search"
          show-expand
          single-expand
        >
          <template v-slot:item.assignedSites="{ item }">
            <v-chip
              v-for="site in item.assignedSites"
              :key="site.name"
              :class="'ma-1 ' + getSiteTagType(site)"
              small
            >
              {{ site.name }}
            </v-chip>
          </template>
          <template v-slot:item.roles="{ item }">
            <v-chip
              v-for="role in item.roles"
              :key="role"
              :class="'ma-1 ' + getUserRoleTagType(role)"
              small
            >
              {{ role }}
            </v-chip>
          </template>
          <template v-slot:item.assignedVessels="{ item }">
            <v-chip
              v-if="item.assignedVessels.length > 0"
              :class="'ma-1 info'"
              small
            >
              {{ item.assignedVessels[0].name }}
            </v-chip>
            <v-chip v-if="item.assignedVessels.length > 1" small>
              +{{ item.assignedVessels.length - 1 }} more</v-chip
            >
          </template>
          <template v-slot:item.assignedNotifications="{ item }">
            <v-chip
              v-if="item.assignedNotifications.length > 0"
              :class="'ma-1 info'"
              small
            >
              {{ item.assignedNotifications[0].siteId }} -
              {{ item.assignedNotifications[0].description }}
            </v-chip>
            <v-chip small v-if="item.assignedNotifications.length > 1">
              +{{ item.assignedNotifications.length - 1 }} more</v-chip
            >
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex">
              <v-icon class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
              <v-icon
                :class="item.userAccountDisabled ? '' : 'hidden'"
                color="red"
                >mdi-lock</v-icon
              >
            </div>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="text-start pa-4">
              <ul style="list-style-type: none">
                <li class="mb-4">
                  <v-icon color="primary" left small>mdi-ferry</v-icon>
                  <span class="primary--text font-weight-bold"
                    >Assigned Vessels:
                  </span>
                  <v-chip
                    v-for="vessel in item.assignedVessels"
                    :key="vessel.name"
                    :class="'ma-1 info'"
                    small
                    >{{ vessel.name }}</v-chip
                  >
                </li>
                <li>
                  <v-icon color="primary" left small>mdi-bell-ring</v-icon>
                  <span class="primary--text font-weight-bold"
                    >Assigned Notifications:
                  </span>
                  <v-chip
                    v-for="notification in item.assignedNotifications"
                    :key="`${notification.value}_${notification.siteId}`"
                    :class="'ma-1'"
                    small
                  >
                    {{ notification.siteId }} -
                    {{ notification.description }}</v-chip
                  >
                </li>
              </ul>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import UserCreateForm from '../components/admin/UserCreateForm.vue';
import UserEditForm from '../components/admin/UserEditForm.vue';
import PageHeader from '../components/ui/PageHeader.vue';
import userRoleTagType from '../mixins/UserRoleTagType';
import siteTagType from '../mixins/SiteTagType';
import ResponsiveSizeHelperMixin from '../mixins/ResponsiveSizeHelperMixin';

export default {
  name: 'UserAdmin',
  components: {
    PageHeader,
    UserCreateForm,
    UserEditForm,
  },
  mixins: [userRoleTagType, siteTagType, ResponsiveSizeHelperMixin],
  data() {
    return {
      title: 'User Admin',
      usersLoading: false,
      dialog: false,
      search: '',
      tableHeaders: [
        { text: 'Name', value: 'username' },
        { text: 'Email', value: 'email' },
        { text: 'Site(s)', value: 'assignedSites' },
        { text: 'Role(s)', value: 'roles' },
        { text: 'Vessel(s)', value: 'assignedVessels' },
        { text: 'Notification(s)', value: 'assignedNotifications' },
        { text: 'Actions', value: 'actions' },
        { text: '', value: 'data-table-expand' },
      ],

    };
  },
  created() {
    this.loadRequiredDataForSite();
  },
  methods: {
    editItem(user) {
      this.usersAdminStore.selectedUser = user;
      window.scrollTo(0, 0);
    },
    close() {
      this.dialog = false;
    },
    loadRequiredDataForSite() {
      this.usersLoading = true;
      const usersFilter = { activeSiteId: this.getActiveSite.id };
      const sitesFilter = { activeSiteId: this.getActiveSite.id };
      const vesselsFilter = { activeSiteId: this.getActiveSite.id };

      this.$store.dispatch('usersAdminStore/loadUsers', usersFilter)
        .finally(() => {
          this.usersLoading = false;
        });
      this.$store.dispatch('usersAdminStore/loadAssignableRoles');
      this.$store.dispatch('usersAdminStore/loadAssignableSites', sitesFilter);
      this.$store.dispatch('usersAdminStore/loadAssignableVessels', vesselsFilter);
      this.$store.dispatch('usersAdminStore/loadAssignableNotifications');
    },
  },
  computed: {
    ...mapState(['usersAdminStore', 'userStore']),
    ...mapGetters({
      isClientConnectedToServer: 'userStore/isClientConnectedToServer',
      getActiveSite: 'userStore/getActiveSite',
    }),
    activeSiteId() {
      const site = this.getActiveSite;
      if (!site) {
        return null;
      }
      return site.id;
    },
    total() {
      return this.usersAdminStore.usersData ? this.usersAdminStore.usersData.users.length : 0;
    },
    users() {
      return this.usersAdminStore.usersData ? this.usersAdminStore.usersData.users : [];
    },
    selectedUser: {
      get() {
        return this.usersAdminStore.selectedUser;
      },
    },
    dataForTable() {
      if (this.users) {
        return this.users.map((x) => ({
          id: x.id,
          username: x.username,
          email: x.email,
          rolesAsString: x.roles.join(', '),
          sitesAsString: (x.assignedSites || []).map((y) => y.name).join(', '),
          vesselsAsString: (x.assignedVessels || []).map((y) => y.name).join(', '),
          roles: x.roles,
          assignedSites: x.assignedSites,
          assignedVessels: x.assignedVessels,
          assignedNotifications: x.assignedNotifications,
          userAccountDisabled: x.userAccountDisabled,
        }));
      }
      return [];
    },
  },
  watch: {
    activeSiteId(newVal) {
      this.loadRequiredDataForSite();
    },
  },
};
</script>

<style scoped>
.search-field {
  max-width: 350px;
}
</style>
